import { manifestRows } from '../../tarifs/model/types';
import type { ManifestBlock } from '../../tarifs/model/manifest-form';

/**
 * PROVISIONAL. The stand-in for a capability that does not exist yet — read the whole comment
 * before extending it.
 *
 * # The problem
 *
 * A version's manifest names documents, workflows and settings by ERN. Three of the four blocks
 * therefore need candidates, and there is nowhere to get them:
 *
 *  - **documentservice and workflowservice do not exist.** There is no service to ask.
 *  - **The asset registry port cannot enumerate.** It answers exactly one question — "is this ref
 *    vouched for?" — because that is what the publish gate needs. Adding a `ListRefs` RPC to a
 *    service that cannot serve it would be inventing an API to make a dropdown look finished.
 *
 * # What this does instead
 *
 * It offers the ERNs THIS OWNER'S OWN PUBLISHED VERSIONS ALREADY PIN. Nothing is invented
 * (CLAUDE.md rule 8b): every option is a ref that a previously published, gate-passed version of
 * this very tarif carries, which is also the most likely thing the next version wants. A manifest
 * is authored by taking the last one and moving a pin, and this is that fact turned into a
 * control.
 *
 * It is honest about its limits, and the limits are the point:
 *
 *  - A brand-new tarif has no published versions, so the dropdowns are EMPTY. That is the true
 *    answer — the console knows of no refs for it — and the escape hatch below is how the first
 *    one is named.
 *  - A ref no previous version used will never appear. Same answer, same reason.
 *
 * So `PublishWizard`'s `key = ern` textarea stays, as the escape hatch rather than as the primary
 * control, and every list marks itself provisional in the UI.
 *
 * # What replaces it
 *
 * A real `ListRefs` capability, the day documentservice and workflowservice exist and the asset
 * registry can enumerate. Recorded as OPEN-QUESTIONS OQ-14. Delete this file then; the field it
 * feeds takes a candidate list and does not care where it came from.
 */

export interface RefCandidate {
  /** The map key a previous version used — `policy_document`, `enrolment`. */
  readonly key: string;
  readonly ern: string;
  /** The service that owns it, for grouping — `documentservice`. */
  readonly service: string;
}

/**
 * Candidates for one manifest block, gathered from published manifests, newest first.
 *
 * De-duplicated on `key = ern`: two versions pinning the same document is the normal case, not two
 * options. The `key` is carried alongside the ERN because a manifest entry is a PAIR — choosing
 * `policy_document = documentservice:tpl/…` fills both halves, and offering the ERN alone would
 * leave the operator to remember what it was called last time.
 */
export function provisionalCandidates(
  block: ManifestBlock,
  manifests: readonly unknown[],
): RefCandidate[] {
  const seen = new Set<string>();
  const candidates: RefCandidate[] = [];

  // Newest first: `manifests` arrives oldest-first from the version list, and the most recently
  // published pin is the one most likely to be wanted again.
  for (const manifest of [...manifests].reverse()) {
    for (const row of manifestRows(manifest)) {
      if (row.block !== block) continue;
      const identity = `${row.key}=${row.ern}`;
      if (seen.has(identity)) continue;
      seen.add(identity);
      candidates.push({ key: row.key, ern: row.ern, service: row.service });
    }
  }
  return candidates;
}
